import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AI_DISCLOSURE_VERSION } from '@shared/ai-disclosure.ts';
import { aiDisclosureApi, type AiDisclosure } from '../api/ai-disclosure.ts';
import { ConfirmDialog } from './ui.tsx';
import { useSession } from '../state/session.tsx';
import { offlineSessionIdentity } from '../state/offline-cache.ts';

/**
 * Current-purpose consent before sending account data to an AI provider.
 *
 * `ensure` resolves once the current version is accepted, opening the notice
 * first when it is not. Callers render `dialog` somewhere in their tree.
 */
export function useAiDisclosure() {
  const { t } = useTranslation();
  const session = useSession();
  const identity = JSON.stringify([
    offlineSessionIdentity(),
    session.user?.id,
    session.user?.role,
    session.user?.permissions,
    session.company?.id,
    session.company?.accessLocked,
    session.company?.effectivePlan,
    session.company?.planFeatures,
    session.currentLocation?.id,
    session.impersonatedBy,
  ]);
  const scope = useRef({ identity, epoch: 0 });
  if (scope.current.identity !== identity) scope.current = { identity, epoch: scope.current.epoch + 1 };
  const active = useRef(true);
  const queryKey = ['ai-disclosure', identity, scope.current.epoch] as const;
  const queryClient = useQueryClient();
  // An operator signed in as this person from /admin may read the notice but
  // not accept it: acceptance is the account holder's own consent, and the
  // server refuses it from a support session. Use the same current session
  // that defines the consent identity above.
  const supportSession = Boolean(session.impersonatedBy);
  const query = useQuery({
    queryKey,
    queryFn: () => aiDisclosureApi.get(),
    staleTime: 5 * 60 * 1000,
    enabled: Boolean(session.user?.id && session.company?.id),
  });
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<AiDisclosure | null>(null);
  const [declined, setDeclined] = useState(false);
  const [noticeEpoch, setNoticeEpoch] = useState(-1);
  const pending = useRef<{ resolve: () => void; reject: (error: Error) => void; current: () => boolean } | null>(null);
  const inflight = useRef<Promise<void> | null>(null);

  function settle(accepted: boolean) {
    const waiter = pending.current;
    pending.current = null;
    setOpen(false);
    setBusy(false);
    setError(null);
    if (!waiter) return;
    if (accepted) waiter.resolve();
    else waiter.reject(new Error('declined'));
  }

  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
      pending.current?.reject(new Error('stale'));
      pending.current = null;
      inflight.current = null;
    };
  }, []);
  useEffect(() => {
    if (pending.current && !pending.current.current()) {
      pending.current.reject(new Error('stale'));
      pending.current = null;
      inflight.current = null;
      setOpen(false);
      setBusy(false);
      setError(null);
    }
  });

  async function ensure(force = false, callerCurrent: () => boolean = () => true): Promise<void> {
    const captured = scope.current;
    const currentScope = () =>
      active.current &&
      scope.current.epoch === captured.epoch &&
      offlineSessionIdentity() === JSON.parse(captured.identity)[0] &&
      callerCurrent();
    const check = () => {
      if (!currentScope()) throw new Error('stale');
    };
    check();
    // A declined notice must not pop again on the next sync tick or handover.
    // A button that sends something passes force so "Not now" can be undone.
    if (declined && !force) throw new Error('declined');
    if (force) setDeclined(false);
    let current = query.data ?? null;
    // The bundled version only decides whether a cached acceptance is stale.
    // A cached or server acceptance for an older purpose cannot authorize
    // inbox data. A newer server notice needs matching, updated client copy.
    if (query.error || !(current?.accepted && current.version === AI_DISCLOSURE_VERSION)) {
      const result = await query.refetch();
      check();
      if (result.error) throw new Error(t('aiDisclosure.loadFailed'));
      current = result.data ?? null;
      if (current?.accepted && current.version === AI_DISCLOSURE_VERSION) return;
    } else {
      return;
    }
    if (!current || current.version !== AI_DISCLOSURE_VERSION) throw new Error(t('aiDisclosure.loadFailed'));
    if (inflight.current) {
      await inflight.current;
      check();
      return;
    }
    setNotice(current);
    setNoticeEpoch(captured.epoch);
    setError(null);
    setOpen(true);
    const promise = new Promise<void>((resolve, reject) => {
      pending.current = { resolve, reject, current: currentScope };
    });
    inflight.current = promise;
    void promise.then(
      () => {
        if (inflight.current === promise) inflight.current = null;
      },
      () => {
        if (inflight.current === promise) inflight.current = null;
      },
    );
    await promise;
    check();
  }

  async function accept() {
    const waiter = pending.current;
    if (!notice || busy || supportSession || !waiter?.current() || notice.version !== AI_DISCLOSURE_VERSION) return;
    setBusy(true);
    setError(null);
    try {
      const saved = await aiDisclosureApi.accept(notice.version);
      if (!waiter.current()) return;
      if (!saved.accepted || saved.version !== AI_DISCLOSURE_VERSION) throw new Error(t('aiDisclosure.saveFailed'));
      queryClient.setQueryData(queryKey, saved);
      settle(true);
    } catch (caught) {
      if (!waiter.current()) return;
      setBusy(false);
      setError(caught instanceof Error ? caught.message : t('aiDisclosure.saveFailed'));
    }
  }

  const dialog =
    open && notice && noticeEpoch === scope.current.epoch ? (
      <ConfirmDialog
        title={t('aiDisclosure.title')}
        confirmLabel={t('aiDisclosure.confirm')}
        cancelLabel={t('aiDisclosure.cancel')}
        confirmDisabled={supportSession}
        busy={busy}
        error={error}
        onConfirm={() => void accept()}
        onCancel={() => {
          setDeclined(true);
          settle(false);
        }}
      >
        {supportSession ? (
          <p role="status" className="mb-3 font-medium">
            {t('aiDisclosure.supportSession')}
          </p>
        ) : null}
        <p>{t('aiDisclosure.body')}</p>
        <ul className="mt-3 flex flex-col gap-3">
          {notice.providers.map((provider) => (
            <li key={provider.id}>
              <strong className="block">{provider.name}</strong>
              <span className="text-sm text-[color:var(--color-muted)]">
                {t(`aiDisclosure.providers.${provider.id}.usedFor`)}
              </span>
              <span className="mt-1 block text-sm">{t(`aiDisclosure.providers.${provider.id}.sent`)}</span>
            </li>
          ))}
        </ul>
      </ConfirmDialog>
    ) : null;

  return { ensure, dialog, declined };
}
